import React, { useEffect, useRef } from 'react';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { TrendingUp, TrendingDown, Users, Clock, AlertTriangle } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Dashboard = () => {
  const metrics = [
    {
      icon: TrendingUp,
      value: '98.2%',
      label: 'Audit accuracy',
      delta: '↑ from 92% baseline',
      trend: 'up',
      color: 'bg-white border-2 border-green-200',
    },
    {
      icon: Users,
      value: '2,041',
      label: 'Active enterprise users',
      delta: '↑ 41 this month',
      trend: 'up',
      color: 'bg-white border-2 border-blue-200',
    },
    {
      icon: Clock,
      value: '13 min',
      label: 'Avg dashboard refresh',
      delta: '↓ 40% vs baseline',
      trend: 'up',
      color: 'bg-white border-2 border-cyan-200',
    },
    {
      icon: AlertTriangle,
      value: '4',
      label: 'Open access anomalies',
      delta: '↑ 2 since yesterday',
      trend: 'down',
      color: 'bg-white border-2 border-red-200',
    },
  ];

  const auditChartData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6', 'Week 7', 'Week 8', 'Week 9', 'Week 10', 'Week 11', 'Week 12'],
    datasets: [
      {
        label: 'Audit Accuracy',
        data: [92.0, 92.3, 92.8, 93.5, 94.1, 94.8, 95.4, 95.9, 96.5, 97.1, 97.7, 98.2],
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Baseline',
        data: Array(12).fill(92),
        borderColor: 'rgb(248, 113, 113)',
        backgroundColor: 'transparent',
        borderDash: [5, 5],
        pointRadius: 0,
      },
    ],
  };

  const sourceChartData = {
    labels: ['Azure AD', 'Saviynt', 'LDAP'],
    datasets: [
      {
        data: [58, 30, 12],
        backgroundColor: [
          'rgba(96, 165, 250, 0.8)',
          'rgba(167, 139, 250, 0.8)',
          'rgba(251, 146, 60, 0.8)',
        ],
        borderColor: [
          'rgb(96, 165, 250)',
          'rgb(167, 139, 250)',
          'rgb(251, 146, 60)',
        ],
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        min: 90,
        max: 100,
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
      },
    },
  };

  const reconciliationData = [
    { system: 'Azure Active Directory', lastSync: '05:58', status: 'OK' },
    { system: 'Saviynt IGA', lastSync: '06:01', status: 'OK' },
    { system: 'LDAP Directory', lastSync: '06:03', status: 'Lag' },
    { system: 'Snowflake Audit', lastSync: '06:00', status: 'OK' },
    { system: 'Power BI Pipeline', lastSync: '06:13', status: 'OK' },
  ];

  const slaData = [
    { label: 'Standard requests (48h)', percentage: 94 },
    { label: 'P1 priority access (4h)', percentage: 100 },
    { label: 'Audit trail complete', percentage: 100 },
    { label: 'Auto-escalations fired', percentage: 6, warning: true },
  ];

  return (
    <section id="dashboard" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-800 border-blue-200 font-semibold">Data Visualization</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              IAM Compliance Monitor — Client A
            </h2>
            <p className="text-lg text-slate-600">
              Identity & Access Management · Audit Period: Q1 2026 · Refreshed nightly 06:00 EST
            </p>
            <Badge variant="outline" className="mt-4 bg-green-50 text-green-700 border-green-300 font-semibold">
              ● Live
            </Badge>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <Card key={index} className={`${metric.color} border-2`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <Icon size={24} className="text-slate-700" />
                      <div className={`text-xs font-medium ${metric.trend === 'up' ? 'text-green-600' : 'text-pink-600'}`}>
                        {metric.delta}
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-slate-900 mb-1">{metric.value}</div>
                    <div className="text-sm text-slate-600">{metric.label}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Charts Row */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Line Chart */}
            <Card className="border-2 border-slate-200 bg-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Audit accuracy trend (12 weeks)</h3>
                <div className="h-64">
                  <Line data={auditChartData} options={chartOptions} />
                </div>
              </CardContent>
            </Card>

            {/* Doughnut Chart */}
            <Card className="border-2 border-slate-200 bg-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Access by source system</h3>
                <div className="h-64">
                  <Doughnut data={sourceChartData} options={doughnutOptions} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tables Row */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* System Reconciliation Table */}
            <Card className="border-2 border-slate-200 bg-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">System reconciliation</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 text-slate-600 font-semibold">System</th>
                        <th className="text-left py-2 text-slate-600 font-semibold">Last sync</th>
                        <th className="text-left py-2 text-slate-600 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconciliationData.map((row, index) => (
                        <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 text-slate-700">{row.system}</td>
                          <td className="py-3 text-slate-600">{row.lastSync}</td>
                          <td className="py-3">
                            <Badge
                              variant="outline"
                              className={
                                row.status === 'OK'
                                  ? 'bg-green-50 text-green-700 border-green-300'
                                  : 'bg-amber-50 text-amber-700 border-amber-300'
                              }
                            >
                              {row.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* SLA Compliance */}
            <Card className="border-2 border-slate-200 bg-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">SLA compliance</h3>
                <div className="space-y-4">
                  {slaData.map((item, index) => (
                    <div key={index}>
                      <div className="flex justify-between mb-1 text-sm">
                        <span className="text-slate-700">{item.label}</span>
                        <span className={`font-semibold ${item.warning ? 'text-amber-600' : 'text-green-600'}`}>
                          {item.percentage}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full transition-all duration-500 ${
                            item.warning ? 'bg-amber-400' : 'bg-green-400'
                          }`}
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Dashboard;
